import React from 'react';
import styled from 'styled-components';
import { DragOverlay as DndKitDragOverlay } from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';

import { useItemThumbnail } from '../../../hooks';

const snapAboveCursor = ({ activatorEvent, draggingNodeRect, transform }: any) => {
  if (draggingNodeRect && activatorEvent) {
    const coords = getEventCoordinates(activatorEvent);
    if (!coords) return transform;
    return {
      ...transform,
      x: transform.x + coords.x - draggingNodeRect.left,
      y: transform.y + coords.y - draggingNodeRect.top - draggingNodeRect.height - 15,
    };
  }
  return transform;
};

interface DragOverlayProps {
  draggedItems: any[] | null;
}

const StyledDragOverlayContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: white;
  border: 2px solid #2196f3;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: grabbing;
  min-height: 64px;
  width: max-content;
  max-width: 380px;
  pointer-events: none;
`;

const THUMB_W = 64;
const THUMB_H = 36;
const THUMB_OFFSET = 8;

const StyledThumbnailContainer = styled.div<{ $itemCount: number }>`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: ${({ $itemCount }) => THUMB_W + (Math.min($itemCount, 3) - 1) * THUMB_OFFSET}px;
  height: ${({ $itemCount }) => THUMB_H + (Math.min($itemCount, 3) - 1) * THUMB_OFFSET}px;
  position: relative;
  flex-shrink: 0;
`;

const StyledDragText = styled.div`
  font-size: 14px;
  font-weight: 500;
  white-space: normal;
  max-width: 260px;
`;

const DragPreviewThumbnail = ({ item, index }: { item: any; index: number }) => {
  const { thumbnailUrl: queriedThumbnailUrl } = useItemThumbnail({
    itemId: item.originalId || item.id,
    itemType: item.type,
    thumbnailUrl: item.thumbnailUrl,
  });

  const thumbnailUrl = queriedThumbnailUrl || item.thumbnailUrl;

  return (
    <div
      style={{
        position: 'absolute',
        left: `${index * THUMB_OFFSET}px`,
        top: `${index * THUMB_OFFSET}px`,
        width: `${THUMB_W}px`,
        height: `${THUMB_H}px`,
        borderRadius: '4px',
        border: '2px solid black',
        zIndex: 10 - index,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        overflow: 'hidden',
      }}
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt="Thumbnail"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <div
          style={{
            fontSize: '10px',
            color: '#999',
            textAlign: 'center',
          }}
        >
          ?
        </div>
      )}
    </div>
  );
};

export const DragOverlay = ({ draggedItems }: DragOverlayProps) => {
  return (
    <DndKitDragOverlay
      dropAnimation={null}
      modifiers={[snapAboveCursor]}
      style={{ cursor: 'grabbing' }}
    >
      {draggedItems && draggedItems.length > 0 && (
        <StyledDragOverlayContainer>
          <StyledThumbnailContainer $itemCount={draggedItems.length}>
            {draggedItems.slice(0, 3).map((item, index) => (
              <DragPreviewThumbnail key={item.instanceId || `${item.id}-${index}`} item={item} index={index} />
            ))}
          </StyledThumbnailContainer>

          <StyledDragText>
            {draggedItems.length > 1
              ? `Dragging ${draggedItems.length} items...`
              : draggedItems[0].displayName || draggedItems[0].name || 'Dragging...'}
          </StyledDragText>
        </StyledDragOverlayContainer>
      )}
    </DndKitDragOverlay>
  );
};
